import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SportPlayerPicker from "../components/community/SportPlayerPicker";
import RosterSlotGrid from "../components/community/RosterSlotGrid";
import {
  useGetCommunityLineupQuery,
  usePublishCommunityRosterMutation,
  useUpdateCommunityRosterMutation,
} from "../store/api/communityApi";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx } from "../utils/brandAssets";
import { communityRosterPath } from "../utils/community";

const COPY = {
  nba: {
    overline: "All-time starting five",
    subtitle: "Tap the floor. Publish when you want it in Community.",
  },
  nfl: {
    overline: "All-time offense, defense, and special teams",
    subtitle: "Tap either side of the ball, plus kicker, punter, and returner.",
  },
};

const SportLineup = () => {
  const { sport } = useSportWorkspace();
  const navigate = useNavigate();
  const { trackCustomEvent } = useAnalytics();
  const { data, isLoading, error } = useGetCommunityLineupQuery(sport, {
    skip: sport === "mlb",
  });
  const [roster, setRoster] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [picker, setPicker] = useState(null);
  const [notice, setNotice] = useState("");
  const [updateRoster] = useUpdateCommunityRosterMutation();
  const [publishRoster, { isLoading: publishing }] =
    usePublishCommunityRosterMutation();
  const copy = COPY[sport] || COPY.nba;

  useEffect(() => {
    if (!data) return;
    setRoster(data);
    setTitle(data.title);
    setDescription(data.description || "");
  }, [data]);

  const excludeIds = useMemo(
    () =>
      Object.values(roster?.players || {})
        .map((player) => player?.playerId)
        .filter(Boolean),
    [roster]
  );

  const persist = async (patch) => {
    if (!roster) return;
    const next = await updateRoster({ id: roster.id, ...patch }).unwrap();
    setRoster(next);
    return next;
  };

  const handlePublish = async () => {
    try {
      await persist({ title, description });
      const result = await publishRoster(roster.id).unwrap();
      trackCustomEvent("roster_published", { sport, roster_id: roster.id });
      navigate(
        communityRosterPath(sport, result.roster?.slug || result.roster?.id)
      );
    } catch (err) {
      setNotice(err?.data?.error || "Could not publish lineup");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !roster) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        Couldn’t load your lineup.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.16em" }}
          >
            {copy.overline}
          </Typography>
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
            My Lineup
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
            {copy.subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={brandFilledButtonSx}
          disabled={publishing || !roster.complete}
          onClick={handlePublish}
        >
          Publish lineup
        </Button>
      </Box>
      <Box sx={{ display: "grid", gap: 2, mb: 4, maxWidth: 520 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => persist({ title }).catch(() => {})}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={() => persist({ description }).catch(() => {})}
          multiline
          minRows={2}
        />
      </Box>
      <RosterSlotGrid
        roster={roster}
        sport={sport}
        editable
        onSelect={setPicker}
        onClear={(slot) => persist({ players: { [slot.id]: null } })}
      />
      <SportPlayerPicker
        open={Boolean(picker)}
        sport={sport}
        title={picker ? `Pick ${picker.label}` : "Pick a player"}
        slotId={picker?.id}
        excludePlayerIds={excludeIds.filter(
          (id) => id !== roster.players?.[picker?.id]?.playerId
        )}
        onClose={() => setPicker(null)}
        onSelect={async (player) => {
          try {
            await persist({ players: { [picker.id]: player.playerId } });
            setPicker(null);
          } catch (err) {
            setNotice(err?.data?.error || "Could not update player");
          }
        }}
      />
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
};

export default SportLineup;
